import React from 'react';
import { Building2, MapPin, Truck, FileText, Globe, Trash2, Calculator, Mail } from 'lucide-react';
import './TopScannerIndicator.css';

interface TopScannerIndicatorProps {
  scannerStatus: 'ready' | 'processing' | string;
  selectedClient: any | null;
  selectedAuthorizedPerson: any | null;
  selectedShippingAddress: string | null;
  generateInvoice: boolean;
  isTaxFree: boolean;
  /** Direccion a la que mandar la factura; '' si no se conoce ninguna. */
  invoiceEmail: string;
  /** Si la factura se manda al cobrar. La direccion se conserva aunque esto sea false. */
  sendInvoiceEmail: boolean;
  currentCashier: any;
  setShowClientModal: (show: boolean) => void;
  setShowShippingModal: (show: boolean) => void;
  setGenerateInvoice: (val: boolean) => void;
  setIsTaxFree: (val: boolean) => void;
  setSendInvoiceEmail: (val: boolean) => void;
  /** Abre el modal para teclear la direccion de esta factura. */
  onEditInvoiceEmail: () => void;
  setSelectedClient: (client: any | null) => void;
  setSelectedAuthorizedPerson: (person: any | null) => void;
  setSelectedShippingAddress: (addr: string | null) => void;
  isNumpadHidden: boolean;
  onShowNumpad: () => void;
}

export const TopScannerIndicator: React.FC<TopScannerIndicatorProps> = ({
  scannerStatus,
  selectedClient,
  selectedAuthorizedPerson,
  selectedShippingAddress,
  generateInvoice,
  isTaxFree,
  invoiceEmail,
  sendInvoiceEmail,
  currentCashier,
  setShowClientModal,
  setShowShippingModal,
  setGenerateInvoice,
  setIsTaxFree,
  setSendInvoiceEmail,
  onEditInvoiceEmail,
  setSelectedClient,
  setSelectedAuthorizedPerson,
  setSelectedShippingAddress,
  isNumpadHidden,
  onShowNumpad
}) => {
  return (
    <div className="top-scanner-indicator">
      {/* 1. Scanner Status */}
      <div className="scanner-status-section">
        <div className={`scanner-dot ${scannerStatus === 'ready' ? 'status-ready' : 'status-processing'}`} />
        <div className="scanner-status-info">
          <h2 className="scanner-status-label">Escáner Activo</h2>
          <p className="scanner-status-text">
            {scannerStatus === 'ready' ? 'Esperando...' : 'Procesando...'}
          </p>
        </div>
      </div>

      {/* 2. Client Section (Button + Info) */}
      <div className="client-section">
        <button
          onClick={() => setShowClientModal(true)}
          className={`client-select-btn ${selectedClient ? 'has-client' : 'no-client'}`}
          title={selectedClient ? 'Cambiar Cliente' : 'Seleccionar Cliente'}
        >
          <div className="client-select-icon-wrapper">
            <Building2 className="client-icon" />
          </div>
        </button>

        {selectedClient && (
          <div className="client-info-container">
            <div className="client-details">
              <h2 className="client-details-label">Cliente</h2>
              <div className="client-details-name-row">
                <p className="client-name">{selectedClient.companyName}</p>
                {selectedShippingAddress && (
                  <button
                    onClick={() => setShowShippingModal(true)}
                    className="shipping-address-btn"
                    title="Editar dirección de envío"
                  >
                    <MapPin className="shipping-address-icon" />
                    <div className="shipping-address-lines">
                      {selectedShippingAddress.split('\n').map((line, i) => (
                        <span key={i} className={`shipping-line ${i === 1 ? 'shipping-line-secondary' : ''}`}>
                          {line}
                        </span>
                      ))}
                    </div>
                  </button>
                )}
              </div>
              <p className="authorized-person">
                {selectedAuthorizedPerson ? `👤 ${selectedAuthorizedPerson.name}` : 'Sin autorizado'}
              </p>
              {/*
                A donde va la factura, para que el cajero lo vea antes de cobrar.
                Se puede pulsar para cambiarla, igual que la direccion de envio.
              */}
              {invoiceEmail && (
                <button
                  onClick={onEditInvoiceEmail}
                  className="authorized-person invoice-email"
                  title="Cambiar el email de esta factura"
                >
                  {`✉ ${invoiceEmail}`}
                </button>
              )}
            </div>

            <div className="client-actions">
              {!selectedShippingAddress && (
                <button
                  onClick={() => setShowShippingModal(true)}
                  className="action-btn shipping-btn"
                  title="Realizar Envío"
                >
                  <Truck className="action-icon" />
                </button>
              )}

              <button
                onClick={() => {
                  const nextVal = !generateInvoice;
                  setGenerateInvoice(nextVal);
                  if (!nextVal) setIsTaxFree(false);
                }}
                disabled={!!selectedShippingAddress || !!selectedClient}
                className={`action-btn invoice-btn ${(generateInvoice || !!selectedShippingAddress || !!selectedClient) ? 'active' : ''}`}
                title={selectedShippingAddress ? "Factura obligatoria para envíos" : selectedClient ? "Con cliente siempre se emite factura" : "Generar Factura"}
              >
                <FileText className="action-icon" />
                {(generateInvoice || !!selectedShippingAddress || !!selectedClient) && (
                  <div className="action-indicator">
                    <div className="action-indicator-dot" />
                  </div>
                )}
              </button>

              {/*
                Mandar la factura por email. Enciende y apaga como los toggles de
                al lado; solo abre el modal cuando no hay ninguna direccion que
                usar. Apagarlo no borra la direccion, asi volver a encenderlo no
                obliga a teclearla otra vez.
              */}
              <button
                onClick={onEditInvoiceEmail}
                className={`action-btn email-btn ${invoiceEmail ? 'active' : ''}`}
                title={
                  invoiceEmail
                    ? `Cambiar el email de la factura (${invoiceEmail})`
                    : 'Escribir un email para esta factura'
                }
              >
                <Mail className="action-icon" />
                {invoiceEmail && (
                  <div className="action-indicator">
                    <div className="action-indicator-dot" />
                  </div>
                )}
              </button>

              <button
                onClick={() => {
                  const nextVal = !isTaxFree;
                  setIsTaxFree(nextVal);
                  if (nextVal) setGenerateInvoice(true);
                }}
                className={`action-btn tax-free-btn ${isTaxFree ? 'active' : ''}`}
                title="Compra Tax Free"
              >
                <Globe className="action-icon" />
                {isTaxFree && (
                  <div className="action-indicator">
                    <div className="action-indicator-dot" />
                  </div>
                )}
              </button>

              <button
                onClick={() => {
                  setSelectedClient(null);
                  setSelectedAuthorizedPerson(null);
                  setSelectedShippingAddress(null);
                  setGenerateInvoice(false);
                  setIsTaxFree(false);
                  setSendInvoiceEmail(false);
                }}
                className="action-btn clear-client-btn"
                title="Anular selección de cliente"
              >
                <Trash2 className="action-icon" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 4. Cashier Info */}
      <div className="cashier-section">
        <div className="cashier-info">
          <h2 className="cashier-label">Operador</h2>
          <p className="cashier-name">{currentCashier?.name}</p>
        </div>
      </div>

      {/* 5. Show Numpad Button */}
      {isNumpadHidden && (
        <div className="show-numpad-section" style={{ display: 'flex', alignItems: 'center', marginLeft: '1rem' }}>
          <button
            onClick={onShowNumpad}
            className="simulate-btn"
          >
            <Calculator className="simulate-icon" />
            <span className="simulate-label">Teclado</span>
          </button>
        </div>
      )}
    </div>
  );
};
